import { useTenant } from "hooks/settings";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  use,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  GcsTokenResponse,
  useGetGcsReadTokenQuery,
} from "src/store/api/generatedApi";

const REFRESH_MARGIN_MS = 60_000;
const TOKEN_RETRY_MAX_ATTEMPTS = 3;
const TOKEN_RETRY_BASE_DELAY_MS = 1_000;

// The read-token query hook a provider sources its downscoped token from. The
// default is the video token (ts/ + public/); the radio player passes its own
// so the token is scoped to the radio/ prefix instead.
type UseTokenQuery = typeof useGetGcsReadTokenQuery;

interface GcsTokenContextValue {
  gcsToken: GcsTokenResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refresh: () => void;
  // Which query produced this token, so a nested EnsureGcsTokenExists can tell
  // whether an outer provider is already the scope it needs.
  sourceQuery: UseTokenQuery;
}

const GcsTokenContext = createContext<GcsTokenContextValue | undefined>(
  undefined
);

const tokenExpiresAt = (token: GcsTokenResponse): number | null => {
  if (!token.expires_at) return null;
  const timestamp = Date.parse(token.expires_at);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const isTokenExpired = (token: GcsTokenResponse): boolean => {
  const expiresAt = tokenExpiresAt(token);
  if (expiresAt == null) return false;
  return expiresAt <= Date.now();
};

/**
 * You can nest EnsureGcsTokenExists; an outer provider is reused only when it
 * was created from the same token query, so a video-scoped page-level provider
 * does not swallow a nested radio-scoped one. This lets you hoist a shared token
 * further out while still requesting a differently-scoped token where needed.
 */
export const EnsureGcsTokenExists = ({
  children,
  useTokenQuery = useGetGcsReadTokenQuery,
}: PropsWithChildren<{ useTokenQuery?: UseTokenQuery }>) => {
  const existingContext = use(GcsTokenContext);

  // Reuse an outer provider only if it already sources the scope we need;
  // otherwise create a nested provider for this scope.
  if (existingContext?.sourceQuery === useTokenQuery) {
    return children;
  }

  return (
    <GcsTokenProvider useTokenQuery={useTokenQuery}>
      {children}
    </GcsTokenProvider>
  );
};

const GcsTokenProvider = ({
  children,
  useTokenQuery = useGetGcsReadTokenQuery,
}: PropsWithChildren<{ useTokenQuery?: UseTokenQuery }>) => {
  const tenantName = useTenant();

  const [cachedToken, setCachedToken] = useState<GcsTokenResponse | undefined>(
    undefined
  );
  const [forceRefresh, setForceRefresh] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const shouldFetch = !cachedToken || forceRefresh;

  const { data, error, isLoading, isFetching } = useTokenQuery(
    { tenantName },
    { skip: !shouldFetch }
  );

  useEffect(() => {
    if (data) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- async token fetch result handling
      setCachedToken(data);
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- async token fetch result handling
      setForceRefresh(false);
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- async token fetch result handling
      setRetryAttempt(0);
      return;
    }
    if (!error) return;
    if (retryAttempt >= TOKEN_RETRY_MAX_ATTEMPTS) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- async token fetch result handling
      setForceRefresh(false);
      return;
    }
    const delayMs = TOKEN_RETRY_BASE_DELAY_MS * Math.pow(2, retryAttempt);
    const retryTimeoutId = window.setTimeout(() => {
      setRetryAttempt((attempt) => attempt + 1);
      setForceRefresh(true);
    }, delayMs);
    return () => {
      window.clearTimeout(retryTimeoutId);
    };
  }, [data, error, retryAttempt]);

  // Clear the cached token if it has expired
  useEffect(() => {
    if (!cachedToken) return;
    const expiresAt = tokenExpiresAt(cachedToken);
    if (expiresAt == null) return;
    if (typeof window === "undefined") return;

    const now = Date.now();
    if (expiresAt <= now) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- async token expiry management
      setCachedToken(undefined);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCachedToken(undefined);
    }, expiresAt - now);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [cachedToken]);

  // Force a refresh if the token is about to expire
  useEffect(() => {
    if (!cachedToken) return;
    const expiresAt = tokenExpiresAt(cachedToken);
    if (expiresAt == null) return;
    if (typeof window === "undefined") return;

    const refreshAt = expiresAt - REFRESH_MARGIN_MS;
    if (refreshAt <= Date.now()) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- async token refresh scheduling
      setForceRefresh(true);
      return;
    }

    const refreshTimeoutId = window.setTimeout(() => {
      setForceRefresh(true);
    }, refreshAt - Date.now());

    return () => {
      window.clearTimeout(refreshTimeoutId);
    };
  }, [cachedToken]);

  const refresh = useCallback(() => {
    setRetryAttempt(0);
    setForceRefresh(true);
  }, []);

  const usableToken = useMemo(() => {
    if (!cachedToken) return undefined;
    if (isTokenExpired(cachedToken)) {
      return undefined;
    }
    return cachedToken;
  }, [cachedToken]);

  const _isLoading: boolean = useMemo(() => {
    if (usableToken) return false;
    if (!shouldFetch) return false;
    return isLoading || isFetching;
  }, [usableToken, shouldFetch, isLoading, isFetching]);

  const contextValue = useMemo<GcsTokenContextValue>(
    () => ({
      gcsToken: usableToken,
      isLoading: _isLoading,
      isFetching,
      error: error,
      refresh,
      sourceQuery: useTokenQuery,
    }),
    [usableToken, _isLoading, isFetching, error, refresh, useTokenQuery]
  );

  return <GcsTokenContext value={contextValue}>{children}</GcsTokenContext>;
};

export const useGcsToken = (): GcsTokenContextValue => {
  const context = use(GcsTokenContext);
  if (!context) {
    throw new Error("useGcsToken must be used within a GcsTokenProvider");
  }

  return context;
};
